import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { RadarData } from '../../types/gymnasium';

// Matches radar chart colors — no pink, red, or green
const RADAR_COLORS = ['#7c3aed', '#2563eb', '#d97706', '#0891b2'];

interface Props {
  radarData: RadarData;
}

const ComparisonTable: React.FC<Props> = ({ radarData }) => {
  const { darkMode } = useTheme();

  const { personas, categories } = radarData;

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-blue-600' : 'bg-white border-blue-500 shadow-sm'
  }`;

  return (
    <div className={sectionClass}>
      <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Score Breakdown</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={`text-left py-2 px-3 text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Category</th>
              {personas.map((p, i) => (
                <th key={p.persona_id} className="text-center py-2 px-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: RADAR_COLORS[i % RADAR_COLORS.length] }} />
                    <span className="text-xs font-medium" style={{ color: RADAR_COLORS[i % RADAR_COLORS.length] }}>
                      {p.name.replace(/_/g, ' ')}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => {
              const vals = personas.map(p => p.scores[cat] || 0);
              const maxVal = Math.max(...vals);
              return (
                <tr key={cat} className={`border-t ${darkMode ? 'border-gray-700/30' : 'border-gray-100'}`}>
                  <td className={`py-2 px-3 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {cat.replace(/_/g, ' ')}
                  </td>
                  {personas.map((p, i) => {
                    const val = p.scores[cat] || 0;
                    const isMax = val === maxVal && maxVal > 0 && vals.filter(v => v === maxVal).length === 1;
                    const color = RADAR_COLORS[i % RADAR_COLORS.length];
                    return (
                      <td key={p.persona_id} className="text-center py-2 px-3">
                        <span
                          className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded ${
                            isMax
                              ? darkMode ? 'bg-white/10' : 'bg-gray-100'
                              : ''
                          }`}
                          style={{ color: isMax ? color : undefined }}
                        >
                          {val.toFixed(1)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonTable;
